import {
  Component,
  Input,
  Output,
  EventEmitter,
  ViewEncapsulation,
  OnChanges,
  SimpleChanges,
  ViewChild,
  TemplateRef,
  AfterViewInit,
  ChangeDetectorRef,
  HostListener,
  inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { LeadershipMember, HackathonTrack } from '../leadership.component';
import { metricKeyToLabel } from '../../../shared/utils/metric-key.util';
import {
  TableComponent,
  TableColumn,
  withCellTemplates,
} from '../../../shared/components/primeng/table/table.component';

const HACKATHON_METRICS_LOWER_IS_BETTER = new Set(['elapsed_seconds', 'fnr', 'fpr']);

interface MetricLegendEntry {
  label: string;
  role: 'primary' | 'secondary' | 'available';
  description: string;
}

interface MetricLegend {
  /** Keywords matched (case-insensitive) against the active dataset's label. */
  match: RegExp;
  title: string;
  metrics: MetricLegendEntry[];
}

/**
 * Hardcoded from each benchmark dataset's Croissant metadata (`platform.metrics`).
 * Explains why the leaderboard shows a different number/mix of metric columns per dataset:
 * every condition reports a primary + secondary pair plus a varying list of "available" metrics.
 */
const HACKATHON_METRIC_LEGENDS: MetricLegend[] = [
  {
    match: /renal|kidney/i,
    title: 'Renal Health — kidney detection',
    metrics: [
      { label: 'mAP@[0.5:0.95]', role: 'primary', description: 'COCO-style mean average precision averaged over IoU thresholds 0.50–0.95. The headline ranking metric.' },
      { label: 'GIoU-matched mAP@[0.5:0.95]', role: 'primary', description: 'Same sweep, but matches predictions to ground truth by Generalized IoU, so near-misses are scored rather than collapsed to zero.' },
      { label: 'Efficiency (FPS / GMACs)', role: 'secondary', description: 'Throughput per unit of compute — a model doing 100 frames/s at 100 GMACs scores 1.00.' },
      { label: 'AP@0.5', role: 'available', description: 'Average precision at the lenient IoU threshold of 0.50.' },
      { label: 'AP@0.75', role: 'available', description: 'Average precision at the strict IoU threshold of 0.75.' },
      { label: 'Mean IoU', role: 'available', description: 'Mean intersection-over-union across all images.' },
      { label: 'Detection rate', role: 'available', description: 'Fraction of images where the model emitted at least one box.' },
      { label: 'Precision@0.5', role: 'available', description: 'Precision at IoU 0.50.' },
      { label: 'Recall@0.5', role: 'available', description: 'Recall at IoU 0.50.' },
      { label: 'F1@0.5', role: 'available', description: 'F1 at IoU 0.50.' },
    ],
  },
  {
    match: /breast/i,
    title: 'Breast Cancer — density classification',
    metrics: [
      { label: 'Macro F1', role: 'primary', description: 'F1 computed separately for each density class (A–D) and averaged equally, so the rare class D counts as much as the common ones.' },
      { label: 'QWK (Quadratic Weighted Kappa)', role: 'primary', description: 'Agreement with the radiologist label, corrected for chance and penalising errors by the square of their distance (A-vs-D costs far more than A-vs-B).' },
      { label: 'Efficiency (FPS / GMACs)', role: 'secondary', description: 'Throughput per unit of compute — a model doing 100 frames/s at 100 GMACs scores 1.00.' },
      { label: 'Accuracy', role: 'available', description: 'Fraction of cases whose predicted density class exactly matches the radiologist label.' },
      { label: 'Macro F2', role: 'available', description: 'F2 (recall weighted twice as heavily as precision), per class, averaged equally.' },
      { label: 'Weighted F2', role: 'available', description: 'F2 per class, averaged weighted by each class’s number of cases.' },
      { label: 'Sensitivity (Recall)', role: 'available', description: 'Per-class TP / (TP + FN), macro-averaged over classes.' },
      { label: 'Specificity', role: 'available', description: 'Per-class TN / (TN + FP), macro-averaged over classes.' },
      { label: 'PPV', role: 'available', description: 'Per-class precision, TP / (TP + FP), macro-averaged over classes.' },
      { label: 'NPV', role: 'available', description: 'Per-class TN / (TN + FN), macro-averaged over classes.' },
      { label: 'AUC', role: 'available', description: 'Area under the ROC curve, one-vs-rest per class, macro-averaged.' },
    ],
  },
  {
    match: /oral/i,
    title: 'Oral Cancer — lesion screening',
    metrics: [
      { label: 'F2 Score', role: 'primary', description: 'Weights recall twice as heavily as precision — chosen because missing a suspicious lesion costs more than a false alarm.' },
      { label: 'NPV', role: 'primary', description: 'Of all images called Non-suspicious, the fraction that truly are. High NPV means a Non-suspicious call can be trusted for screening.' },
      { label: 'Efficiency (FPS / GMACs)', role: 'secondary', description: 'Throughput per unit of compute — a model doing 100 frames/s at 100 GMACs scores 1.00.' },
      { label: 'Sensitivity (Recall)', role: 'available', description: 'Fraction of truly Suspicious cases the model flags as Suspicious, TP / (TP + FN).' },
      { label: 'Specificity', role: 'available', description: 'Fraction of truly Non-suspicious cases the model calls Non-suspicious, TN / (TN + FP).' },
      { label: 'PPV', role: 'available', description: 'Of all images called Suspicious, the fraction that truly are, TP / (TP + FP).' },
    ],
  },
];

@Component({
  selector: 'app-leadership-provider',
  standalone: true,
  imports: [CommonModule, TableComponent],
  templateUrl: './provider.component.html',
  styleUrls: ['./provider.component.scss', '../leadership.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class ProviderComponent implements OnChanges, AfterViewInit {
  private readonly cdr = inject(ChangeDetectorRef);

  @Input() leadershipMembers: LeadershipMember[] = [];
  @Input() selectedDesign: 'podium' | 'grid' | 'timeline' | 'magazine' | 'minimal' = 'magazine';
  @Input() providersPagination: any = {};
  @Input() showLoadMore: boolean = true;
  @Input() showEngagementMetrics: boolean = true;
  @Input() headingLabel: string = 'Provider Leaderboard';
  @Input() primaryMetricLabel: string = 'Published';
  @Input() boardVisualVariant: 'providers' | 'hackathon' = 'providers';
  @Input() hackathonTrackLabel: string = '';
  @Input() hackathonTabs: Array<{ key: HackathonTrack; label: string }> = [];
  @Input() activeHackathonTrack: HackathonTrack = '';
  @Input() hackathonMetricColumns: string[] = [];
  @Input() hackathonTableRows: Record<string, unknown>[] = [];
  @Output() loadMore = new EventEmitter<void>();
  @Output() hackathonTrackChange = new EventEmitter<HackathonTrack>();
  @Output() downloadHackathonCsv = new EventEmitter<void>();

  tableColumns: TableColumn[] = [];
  sortedTableRows: Record<string, unknown>[] = [];
  sortColumn = 'rank';
  sortDirection: 'asc' | 'desc' = 'asc';
  showMetricsLegend = false;

  @ViewChild('rankCell', { static: false }) rankCellTemplate!: TemplateRef<unknown>;
  @ViewChild('submissionCell', { static: false }) submissionCellTemplate!: TemplateRef<unknown>;
  @ViewChild('metricCell', { static: false }) metricCellTemplate!: TemplateRef<unknown>;
  @ViewChild('submittedCell', { static: false }) submittedCellTemplate!: TemplateRef<unknown>;
  private hackathonCellTemplatesReady = false;

  get topThree(): LeadershipMember[] {
    return this.leadershipMembers.filter(m => m.rank <= 3).sort((a, b) => a.rank - b.rank);
  }

  get isHackathonLoading(): boolean {
    return Boolean(this.providersPagination?.isLoading);
  }

  /** Metric legend for the active dataset, matched by its label; falls back to a generic note. */
  get activeMetricLegend(): MetricLegend | null {
    const label = this.hackathonTrackLabel || '';
    return HACKATHON_METRIC_LEGENDS.find((legend) => legend.match.test(label)) ?? null;
  }

  toggleMetricsLegend(event?: Event): void {
    event?.stopPropagation();
    this.showMetricsLegend = !this.showMetricsLegend;
  }

  closeMetricsLegend(): void {
    this.showMetricsLegend = false;
  }

  @HostListener('document:keydown.escape')
  onEscapeKey(): void {
    this.closeMetricsLegend();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      changes['hackathonTableRows'] ||
      changes['hackathonMetricColumns'] ||
      changes['boardVisualVariant']
    ) {
      this.buildHackathonTable();
    }
  }

  ngAfterViewInit(): void {
    this.hackathonCellTemplatesReady = true;
    if (this.boardVisualVariant === 'hackathon' && this.tableColumns.length === 0) {
      this.buildHackathonTable();
    } else {
      this.applyHackathonCellTemplates();
    }
  }

  loadMoreProviders(): void {
    this.loadMore.emit();
  }

  formatPrimaryMetricValue(value: number | undefined | null): string {
    const numericValue = Number(value);
    if (
      value === undefined ||
      value === null ||
      Number.isNaN(numericValue) ||
      !Number.isFinite(numericValue)
    ) {
      return this.boardVisualVariant === 'hackathon' ? '0.00' : '0';
    }
    if (this.boardVisualVariant === 'hackathon') {
      return numericValue.toFixed(2);
    }
    return String(numericValue);
  }

  onHackathonTrackChange(trackKey: HackathonTrack): void {
    this.hackathonTrackChange.emit(trackKey);
  }

  onHackathonDatasetSelect(event: Event): void {
    const value = (event.target as HTMLSelectElement | null)?.value ?? '';
    if (value) {
      this.onHackathonTrackChange(value);
    }
  }

  formatMetricLabel(metric: string, short = false): string {
    const labels: Record<string, string> = short
      ? {
          accuracy: 'Acc.',
          f2_score: 'F2',
          npv: 'NPV',
          ppv: 'PPV',
          sensitivity: 'Sens.',
          specificity: 'Spec.',
          auc: 'AUC',
          macro_f1: 'M.F1',
          macro_f2: 'M.F2',
          weighted_f2: 'W.F2',
          qwk: 'QWK',
          fnr: 'FNR',
          fpr: 'FPR',
          elapsed_seconds: 'Runtime (s)',
          submitted_at: 'Date',
          name: 'Name',
          job_id: 'Job ID',
          rank: 'Rank',
        }
      : {
          accuracy: 'Accuracy',
          f2_score: 'F2 Score',
          npv: 'NPV',
          ppv: 'PPV',
          sensitivity: 'Sensitivity',
          specificity: 'Specificity',
          auc: 'AUC',
          macro_f1: 'Macro F1',
          macro_f2: 'Macro F2',
          weighted_f2: 'Weighted F2',
          qwk: 'QWK',
          fnr: 'FNR',
          fpr: 'FPR',
          elapsed_seconds: 'Runtime (s)',
          submitted_at: 'Submitted',
          name: 'Name',
          job_id: 'Job ID',
          rank: 'Rank',
        };
    return labels[metric] ?? metricKeyToLabel(metric);
  }

  formatSubmittedValue(value: unknown): string {
    const parsed = this.parseSubmittedDate(value);
    if (!parsed) {
      return '—';
    }
    const dd = String(parsed.getDate()).padStart(2, '0');
    const mm = String(parsed.getMonth() + 1).padStart(2, '0');
    const yy = String(parsed.getFullYear()).slice(-2);
    const hh = String(parsed.getHours()).padStart(2, '0');
    const min = String(parsed.getMinutes()).padStart(2, '0');
    return `${dd}/${mm}/${yy} ${hh}:${min}`;
  }

  formatSubmittedTitle(value: unknown): string | null {
    const parsed = this.parseSubmittedDate(value);
    if (!parsed) {
      return null;
    }
    return parsed.toLocaleString();
  }

  formatTableCellValue(key: string, value: unknown): string {
    if (value === null || value === undefined || value === '') {
      return '—';
    }
    if (key === 'submitted_at' && typeof value === 'string') {
      return this.formatSubmittedValue(value);
    }
    if (key === 'rank') {
      return String(value);
    }
    if (key === 'name' || key === 'job_id') {
      return String(value);
    }
    const numericValue = Number(value);
    if (!Number.isNaN(numericValue) && Number.isFinite(numericValue)) {
      return numericValue.toFixed(2);
    }
    return String(value);
  }

  getRankClass(rank: unknown): string {
    const numericRank = Number(rank);
    if (numericRank === 1) return 'leaderboard-rank--gold';
    if (numericRank === 2) return 'leaderboard-rank--silver';
    if (numericRank === 3) return 'leaderboard-rank--bronze';
    return '';
  }

  /** Position in the current sort order; preserves API rank when sorting by rank column. */
  getDisplayRank(row: Record<string, unknown>, rowIndex: number): number {
    if (this.sortColumn === 'rank') {
      return Number(row['rank']) || rowIndex + 1;
    }
    return rowIndex + 1;
  }

  onHackathonSortChange(event: { column: string; direction: 'asc' | 'desc' }): void {
    this.sortColumn = event.column;
    this.sortDirection = event.direction;
    this.applySort();
  }

  onDownloadHackathonCsv(): void {
    this.downloadHackathonCsv.emit();
  }

  private buildHackathonTable(): void {
    if (this.boardVisualVariant !== 'hackathon') {
      return;
    }

    const columnWidths = this.getHackathonColumnWidths();

    const metricColumns: TableColumn[] = this.hackathonMetricColumns.map((key) => ({
      key,
      label: this.formatMetricLabel(key, true),
      title: this.formatMetricLabel(key, false),
      sortable: true,
      align: 'right' as const,
      width: columnWidths.metric,
    }));

    this.tableColumns = [
      {
        key: 'rank',
        label: 'Rank',
        title: 'Rank',
        sortable: true,
        align: 'center',
        width: columnWidths.rank,
      },
      {
        key: 'name',
        label: 'Name',
        title: 'Name',
        sortable: true,
        width: columnWidths.name,
      },
      ...metricColumns,
      {
        key: 'submitted_at',
        label: this.formatMetricLabel('submitted_at', true),
        title: this.formatMetricLabel('submitted_at', false),
        sortable: true,
        width: columnWidths.submitted,
      },
      {
        key: 'elapsed_seconds',
        label: this.formatMetricLabel('elapsed_seconds', true),
        title: this.formatMetricLabel('elapsed_seconds', false),
        sortable: true,
        align: 'right',
        width: columnWidths.runtime,
      },
    ];

    this.sortedTableRows = [...this.hackathonTableRows];
    this.sortColumn = 'rank';
    this.sortDirection = 'asc';
    this.applySort();
    this.applyHackathonCellTemplates();
  }

  /** Percentage widths that fill the container without horizontal overflow. */
  private getHackathonColumnWidths(): {
    rank: string;
    name: string;
    metric: string;
    submitted: string;
    runtime: string;
  } {
    const metricCount = Math.max(this.hackathonMetricColumns.length, 1);
    const rankPct = 5;
    const namePct = 14;
    const submittedPct = 9;
    const runtimePct = 8;
    const metricPct =
      (100 - rankPct - namePct - submittedPct - runtimePct) / metricCount;

    return {
      rank: `${rankPct}%`,
      name: `${namePct}%`,
      metric: `${metricPct.toFixed(2)}%`,
      submitted: `${submittedPct}%`,
      runtime: `${runtimePct}%`,
    };
  }

  private applyHackathonCellTemplates(): void {
    if (
      !this.hackathonCellTemplatesReady ||
      !this.rankCellTemplate ||
      !this.submissionCellTemplate ||
      !this.metricCellTemplate ||
      !this.submittedCellTemplate
    ) {
      return;
    }

    const metricTemplateKeys = [
      ...this.hackathonMetricColumns,
      'elapsed_seconds',
    ];

    this.tableColumns = withCellTemplates(this.tableColumns, [
      { key: 'rank', cellTemplate: this.rankCellTemplate },
      { key: 'name', cellTemplate: this.submissionCellTemplate },
      { key: 'submitted_at', cellTemplate: this.submittedCellTemplate },
      ...metricTemplateKeys.map((key) => ({
        key,
        cellTemplate: this.metricCellTemplate,
      })),
    ]);
    this.cdr.detectChanges();
  }

  private applySort(): void {
    const column = this.sortColumn;
    const direction = this.sortDirection;
    const lowerIsBetter = HACKATHON_METRICS_LOWER_IS_BETTER.has(column);

    const sorted = [...this.hackathonTableRows].sort((a, b) => {
      let diff = this.compareRowValues(a[column], b[column], column);
      if (lowerIsBetter) {
        diff = -diff;
      }
      if (diff !== 0) {
        return direction === 'asc' ? diff : -diff;
      }
      if (column !== 'rank') {
        const rankDiff = this.compareRowValues(a['rank'], b['rank'], 'rank');
        if (rankDiff !== 0) {
          return rankDiff;
        }
      }
      return String(a['name'] ?? '').localeCompare(String(b['name'] ?? ''));
    });

    this.sortedTableRows = sorted.map((row) => ({ ...row }));
  }

  private compareRowValues(a: unknown, b: unknown, key: string): number {
    if (key === 'submitted_at') {
      const aMs = typeof a === 'string' ? Date.parse(a) : Number.NaN;
      const bMs = typeof b === 'string' ? Date.parse(b) : Number.NaN;
      const aVal = Number.isNaN(aMs) ? Number.POSITIVE_INFINITY : aMs;
      const bVal = Number.isNaN(bMs) ? Number.POSITIVE_INFINITY : bMs;
      return aVal - bVal;
    }

    if (key === 'name' || key === 'job_id') {
      return String(a ?? '').localeCompare(String(b ?? ''));
    }

    const aNum = Number(a);
    const bNum = Number(b);
    const aIsNum = !Number.isNaN(aNum) && Number.isFinite(aNum);
    const bIsNum = !Number.isNaN(bNum) && Number.isFinite(bNum);

    if (aIsNum && bIsNum) {
      return aNum - bNum;
    }

    return String(a ?? '').localeCompare(String(b ?? ''));
  }

  private parseSubmittedDate(value: unknown): Date | null {
    if (value === null || value === undefined || value === '') {
      return null;
    }
    const parsed = typeof value === 'string' ? Date.parse(value) : Number.NaN;
    if (Number.isNaN(parsed)) {
      return null;
    }
    return new Date(parsed);
  }
}
